import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import {
  InboxConversation,
  ConversationMessage,
} from '@/types/residents/marketplaceListing';
import { MARKETPLACE_API, CACHE_TAGS } from '@/constants/residents/marketplaceConstants';
import {
  ApiResponse,
  StartConversationRequest,
  SendMessageRequest,
  GetConversationRequest,
  ConversationPagination,
  BackendConversationResponse,
  BackendConversationWithPaginationResponse,
  BackendMessageResponse,
  buildMessageFormData,
} from './types';

// ============================================================================
// Conversation Data Type
// ============================================================================

export interface ConversationData {
  id: string;
  messages: ConversationMessage[];
  listing: {
    id: string;
    title: string;
    image: string;
  };
  otherUser: {
    id: string;
    name: string;
    initials: string;
    avatarUrl?: string;
  };
  currentUserId: number;
  pagination: ConversationPagination;
}

// ============================================================================
// Messages API
// ============================================================================

export const messagesApi = createApi({
  reducerPath: 'marketplaceMessagesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    CACHE_TAGS.Conversations,
    CACHE_TAGS.Conversation,
  ],
  endpoints: (builder) => ({
    /**
     * Get all conversations (inbox)
     */
    getConversations: builder.query<InboxConversation[], void>({
      query: () => ({
        url: MARKETPLACE_API.messages.conversations,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<BackendConversationResponse[]> | null): InboxConversation[] => {
        if (!response || !response.data) {
          console.warn('getConversations: Empty or null response from API');
          return [];
        }
        return response.data.map((conv) => ({
          id: String(conv.id),
          listing: {
            id: String(conv.listing.id),
            title: conv.listing.title,
            image: conv.listing.images?.[0]?.imageUrl || '',
          },
          otherUser: {
            id: String(conv.otherUserId),
            name: conv.otherUserName || 'Unknown User',
            initials: conv.otherUserInitials || 'U',
            avatarUrl: conv.otherUserAvatarUrl,
          },
          lastMessage: {
            text: conv.lastMessage?.text || '',
            timestamp: conv.lastMessage?.createdAt || conv.lastMessageAt,
            isFromMe: conv.lastMessage?.senderId === conv.currentUserId,
          },
          isUnread: conv.isUnread,
        }));
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Conversations' as const, id })),
              { type: CACHE_TAGS.Conversations, id: 'LIST' },
            ]
          : [{ type: CACHE_TAGS.Conversations, id: 'LIST' }],
    }),

    /**
     * Get a single conversation with messages (supports pagination)
     */
    getConversation: builder.query<ConversationData | null, GetConversationRequest>({
      query: ({ conversationId, pageNum = 0 }) => ({
        url: MARKETPLACE_API.messages.conversationById(conversationId),
        method: 'GET',
        params: { pageNum },
      }),
      transformResponse: (response: BackendConversationWithPaginationResponse | null) => {
        if (!response || !response.data) {
          console.warn('getConversation: Empty or null response from API');
          return null;
        }
        const data = response.data;
        const pagination = response.pagination;
        const currentUserId = data.currentUserId!;
        const otherUserName = data.otherUserName || 'Unknown User';
        return {
          id: String(data.id),
          messages: (data.messages || []).map((msg: BackendMessageResponse) => ({
            id: String(msg.id),
            text: msg.text,
            sender: msg.senderId === currentUserId ? 'user' as const : 'other' as const,
            timestamp: msg.createdAt, // Keep as ISO string for Redux serialization
            files: msg.files?.map((f) => ({
              id: String(f.id),
              url: f.fileUrl,
              name: f.fileName,
            })),
            replyTo: msg.replyTo ? {
              id: String(msg.replyTo.id),
              text: msg.replyTo.text,
              senderName: msg.replyTo.senderId === currentUserId ? 'You' : otherUserName,
            } : undefined,
          })),
          listing: {
            id: String(data.listing?.id),
            title: data.listing?.title,
            image: data.listing?.images?.[0]?.imageUrl || '',
          },
          otherUser: {
            id: String(data.otherUserId),
            name: otherUserName,
            initials: data.otherUserInitials || 'U',
            avatarUrl: data.otherUserAvatarUrl,
          },
          currentUserId,
          pagination: pagination || {
            pageNum: 0,
            pageSize: 50,
            totalMessages: data.messages?.length || 0,
            totalPages: 1,
            hasMore: false,
          },
        };
      },
      // Merge messages from different pages
      serializeQueryArgs: ({ queryArgs }) => {
        return queryArgs.conversationId;
      },
      merge: (currentCache, newData, { arg }) => {
        if (!newData) return currentCache;
        if (!currentCache) return newData;
        
        // If loading first page, replace everything
        if (arg.pageNum === 0) {
          return newData;
        }
        
        // Merge older messages at the beginning (pagination loads older messages)
        const existingMessageIds = new Set(currentCache.messages.map(m => m.id));
        const newMessages = newData.messages.filter(m => !existingMessageIds.has(m.id));
        
        return {
          ...newData,
          messages: [...newMessages, ...currentCache.messages],
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg?.pageNum !== previousArg?.pageNum;
      },
      providesTags: (result, error, { conversationId }) => [{ type: CACHE_TAGS.Conversation, id: conversationId }],
    }),

    /**
     * Start a new conversation about a listing
     */
    startConversation: builder.mutation<{ conversationId: string } | null, StartConversationRequest>({
      query: (data) => {
        // Use FormData if files are present
        if (data.files && data.files.length > 0) {
          const formData = buildMessageFormData(data.message, data.files);
          formData.append('listingId', String(data.listingId));
          return {
            url: MARKETPLACE_API.messages.conversations,
            method: 'POST',
            body: formData,
            formData: true,
          };
        }
        // Otherwise use simple JSON body
        return {
          url: MARKETPLACE_API.messages.conversations,
          method: 'POST',
          body: {
            listingId: Number(data.listingId),
            message: data.message,
          },
        };
      },
      transformResponse: (response: ApiResponse<{ conversationId: number }> | null) => {
        if (!response || !response.data) {
          console.warn('startConversation: Empty or null response from API');
          return null;
        }
        return {
          conversationId: String(response.data.conversationId),
        };
      },
      invalidatesTags: [{ type: CACHE_TAGS.Conversations, id: 'LIST' }],
    }),

    /**
     * Send a message in a conversation
     */
    sendMessage: builder.mutation<ConversationMessage | null, SendMessageRequest>({
      query: ({ conversationId, text, files, replyToId }) => {
        // Use FormData if files are present
        if (files && files.length > 0) {
          return {
            url: MARKETPLACE_API.messages.sendMessage(conversationId),
            method: 'POST',
            body: buildMessageFormData(text, files, replyToId),
            formData: true,
          };
        }
        // Otherwise use simple JSON body
        return {
          url: MARKETPLACE_API.messages.sendMessage(conversationId),
          method: 'POST',
          body: { text, replyToId: replyToId ? Number(replyToId) : undefined },
        };
      },
      transformResponse: (response: ApiResponse<BackendMessageResponse> | null): ConversationMessage | null => {
        if (!response || !response.data) {
          console.warn('sendMessage: Empty or null response from API');
          return null;
        }
        return {
          id: String(response.data.id),
          text: response.data.text,
          sender: 'user',
          timestamp: response.data.createdAt, // Keep as ISO string for Redux serialization
          files: response.data.files?.map((f) => ({
            id: String(f.id),
            url: f.fileUrl,
            name: f.fileName,
          })),
        };
      },
      invalidatesTags: (result, error, { conversationId, listingId }) => {
        const tags: Array<{ type: typeof CACHE_TAGS.Conversation | typeof CACHE_TAGS.Conversations; id: string }> = [
          { type: CACHE_TAGS.Conversation, id: conversationId },
          { type: CACHE_TAGS.Conversations, id: 'LIST' },
        ];
        // Also invalidate listing-based conversation cache when sending from inline conversation
        if (listingId) {
          tags.push({ type: CACHE_TAGS.Conversation, id: `listing-${listingId}` });
          tags.push({ type: CACHE_TAGS.Conversations, id: `listing-${listingId}` });
        }
        return tags;
      },
    }),

    /**
     * Mark a conversation as read
     */
    markConversationAsRead: builder.mutation<void, string>({
      query: (conversationId) => ({
        url: MARKETPLACE_API.messages.markAsRead(conversationId),
        method: 'POST',
      }),
      invalidatesTags: (result, error, conversationId) => [
        { type: CACHE_TAGS.Conversations, id: 'LIST' },
        { type: CACHE_TAGS.Conversation, id: conversationId },
      ],
    }),

    /**
     * Get conversation by listing ID (for buyers to check if they have an existing conversation)
     * Returns the conversation data if exists, null otherwise
     * Supports pagination via pageNum parameter
     */
    getConversationByListing: builder.query<ConversationData | null, string | { listingId: string; pageNum?: number }>({
      query: (arg) => {
        const listingId = typeof arg === 'string' ? arg : arg.listingId;
        const pageNum = typeof arg === 'string' ? 0 : (arg.pageNum || 0);
        return {
          url: MARKETPLACE_API.messages.conversationByListing(listingId),
          method: 'GET',
          params: { pageNum },
        };
      },
      transformResponse: (response: BackendConversationWithPaginationResponse | null) => {
        if (!response || !response.data) {
          return null;
        }
        const data = response.data;
        const pagination = response.pagination;
        const currentUserId = data.currentUserId!;
        const otherUserName = data.otherUserName || 'Unknown User';
        return {
          id: String(data.id),
          messages: (data.messages || []).map((msg: BackendMessageResponse) => ({
            id: String(msg.id),
            text: msg.text,
            sender: msg.senderId === currentUserId ? 'user' as const : 'other' as const,
            timestamp: msg.createdAt,
            files: msg.files?.map((f) => ({
              id: String(f.id),
              url: f.fileUrl,
              name: f.fileName,
            })),
            replyTo: msg.replyTo ? {
              id: String(msg.replyTo.id),
              text: msg.replyTo.text,
              senderName: msg.replyTo.senderId === currentUserId ? 'You' : otherUserName,
            } : undefined,
          })),
          listing: {
            id: String(data.listing?.id),
            title: data.listing?.title,
            image: data.listing?.images?.[0]?.imageUrl || '',
          },
          otherUser: {
            id: String(data.otherUserId),
            name: otherUserName,
            initials: data.otherUserInitials || 'U',
            avatarUrl: data.otherUserAvatarUrl,
          },
          currentUserId,
          pagination: pagination || {
            pageNum: 0,
            pageSize: 50,
            totalMessages: data.messages?.length || 0,
            totalPages: 1,
            hasMore: false,
          },
        };
      },
      providesTags: (result, error, arg) => {
        const listingId = typeof arg === 'string' ? arg : arg.listingId;
        return [{ type: CACHE_TAGS.Conversation, id: `listing-${listingId}` }];
      },
    }),

    /**
     * Get all conversations for a listing (for sellers to see all buyer conversations)
     */
    getListingConversations: builder.query<InboxConversation[], string>({
      query: (listingId) => ({
        url: MARKETPLACE_API.listings.conversations(listingId),
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<BackendConversationResponse[]> | null): InboxConversation[] => {
        if (!response || !response.data) {
          return [];
        }
        return response.data.map((conv) => ({
          id: String(conv.id),
          listing: {
            id: String(conv.listing.id),
            title: conv.listing.title,
            image: conv.listing.images?.[0]?.imageUrl || '',
          },
          otherUser: {
            id: String(conv.otherUserId),
            name: conv.otherUserName || 'Unknown User',
            initials: conv.otherUserInitials || 'U',
            avatarUrl: conv.otherUserAvatarUrl,
          },
          lastMessage: {
            text: conv.lastMessage?.text || '',
            timestamp: conv.lastMessage?.createdAt || conv.lastMessageAt,
            isFromMe: conv.lastMessage?.senderId === conv.currentUserId,
          },
          isUnread: conv.isUnread,
        }));
      },
      providesTags: (result, error, listingId) => [
        { type: CACHE_TAGS.Conversations, id: `listing-${listingId}` },
      ],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetConversationsQuery,
  useLazyGetConversationsQuery,
  useGetConversationQuery,
  useLazyGetConversationQuery,
  useStartConversationMutation,
  useSendMessageMutation,
  useMarkConversationAsReadMutation,
  useGetConversationByListingQuery,
  useLazyGetConversationByListingQuery,
  useGetListingConversationsQuery,
  useLazyGetListingConversationsQuery,
} = messagesApi;

